import React, { useState, useEffect } from 'react';
import { useLocation, Link as RouterLink } from 'react-router-dom';
import { 
  Container, 
  Typography, 
  Box,
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  Tabs,
  Tab,
  Tooltip,
  useTheme,
  Link
} from '@mui/material';
import { tableStyles as themeTableStyles } from '../theme/tableStyles';
import { tooltipStyles, tabStyles, avatarStyles, tabContainerStyles } from '../theme/componentStyles';
import { typography, cellStyles } from '../theme/typography';
import { animalProducts } from '../data/productData';
import { formatProductionTime } from '../utils/productUtils';
import RarityAvatar from '../components/common/RarityAvatar';
import RarityBadge from '../components/common/RarityBadge';

import { animalTypes } from '../utils/animalUtils';

// Direct mapping between product IDs and their source animal IDs
const productToAnimalMap = {
  'eggs': 'birds',
  'milk': 'cattle',
  'wool': 'fuzzies',
  'truffle': 'piggies'
};

// Create mapping from animal display names to their IDs
const animalNameToId = animalTypes.reduce((acc, type) => {
  acc[type.name] = type.id;
  return acc;
}, {});

// Define product types with consistent styling
const productTypes = [
  { 
    name: 'Eggs',
    id: 'eggs',
    filter: prod => prod.id === 'eggs',
    imagePath: '/images/vegetables/egg/common/preview.png'
  },
  { 
    name: 'Milk',
    id: 'milk',
    filter: prod => prod.id === 'milk',
    imagePath: '/images/vegetables/milk/common/preview.png'
  },
  { 
    name: 'Wool',
    id: 'wool',
    filter: prod => prod.id === 'wool',
    imagePath: '/images/beds/fuzzies/common/preview.png'
  },
  { 
    name: 'Truffle',
    id: 'truffle',
    filter: prod => prod.id === 'truffle',
    imagePath: '/images/vegetables/truffle/common/preview.png'
  }
];

const Products = () => {
  const location = useLocation();
  const theme = useTheme();
  const tableStyles = React.useMemo(() => themeTableStyles(theme), [theme]);
  
  // Initialize tab state from URL hash, navigation state, or default to 0
  const [tabValue, setTabValue] = useState(0);

  // Handle URL hash and navigation state changes
  useEffect(() => {
    // Check for tab in navigation state first
    if (location.state?.tab) {
      const tabFromState = productTypes.findIndex(
        type => type.id === location.state.tab
      );
      if (tabFromState >= 0) {
        setTabValue(tabFromState);
        // Clear the state to prevent sticking
        window.history.replaceState({}, '');
        return;
      }
    }

    // Fall back to URL hash
    const hash = window.location.hash.substring(1);
    if (hash) {
      const tabFromHash = productTypes.findIndex(
        type => type.name.toLowerCase() === hash
      );
      if (tabFromHash >= 0) {
        setTabValue(tabFromHash);
        return;
      }
    }

    // Default to first tab if nothing else matches
    if (productTypes.length > 0) {
      setTabValue(0);
    }
  }, [location.state, location.pathname]);

  // Update URL hash when tab changes
  useEffect(() => {
    if (productTypes[tabValue]) {
      const tabName = productTypes[tabValue].name.toLowerCase();
      window.history.replaceState({}, '', `#${tabName}`);
    }
  }, [tabValue]);

  const handleTabChange = (event, newValue) => {
    if (newValue >= 0 && newValue < productTypes.length) {
      setTabValue(newValue);
    }
  };

  // Get filtered products for current tab
  const filteredProducts = productTypes[tabValue] 
    ? animalProducts.filter(productTypes[tabValue].filter) 
    : [];

  return (
    <Box sx={{ 
      ...tabContainerStyles.outer, 
      width: '100%',
      minHeight: '100vh',
      pt: 4,
      pb: 4, // Add bottom padding to match other pages
      overflowX: 'auto',
      '&::-webkit-scrollbar': {
        height: '8px',
      },
      '&::-webkit-scrollbar-track': {
        background: 'rgba(0, 0, 0, 0.1)',
      },
      '&::-webkit-scrollbar-thumb': {
        backgroundColor: 'rgba(0, 0, 0, 0.2)',
        borderRadius: '4px',
      },
    }}>
      <Container maxWidth={false} disableGutters sx={{ ...tabContainerStyles.inner, width: 1200, mx: 'auto' }}>
        <Box sx={{ width: '100%', maxWidth: 1200, mx: 'auto' }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ 
              ...tabStyles.container,
              width: '100%',
              '& .MuiTabs-flexContainer': {
                display: 'flex',
                justifyContent: 'space-around',
                width: '100%',
                gap: 2
              },
              '& .MuiTab-root': {
                minWidth: 'auto',
                flex: 1,
                maxWidth: 'none',
                padding: '6px 8px'
              }
            }}
            aria-label="products tabs"
          >
            {productTypes.map((type) => (
              <Tab
                key={type.name}
                label={
                  <Box sx={tabStyles.content}>
                    <RarityAvatar 
                      src={type.imagePath} 
                      alt={type.name} 
                      size="md"
                      sx={avatarStyles.standard}
                    />
                    <Typography variant="caption" sx={tabStyles.label}>
                      {type.name}
                    </Typography>
                  </Box>
                }
                sx={{ width: '400px', ...tabStyles.root }}
              />
            ))}
          </Tabs>
        </Box>
        <TableContainer sx={{ 
          ...tableStyles.tableContainer, 
          width: '100%',
          maxWidth: 1200,
          mx: 'auto',
          overflow: 'hidden',
          boxShadow: 3,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          background: 'transparent',
          '& .MuiTableCell-root': {
            textAlign: 'center',
            verticalAlign: 'middle',
            lineHeight: 1,
            height: 40,
            minHeight: 40,
            maxHeight: 40,
            padding: '8px 16px'
          },
          '& .MuiTableHead-root .MuiTableCell-root': {
            py: 1,
            px: 2,
            height: 40,
            minHeight: 40,
            maxHeight: 40
          },
          '&:hover': {
            boxShadow: 4
          }
        }}>
          <Table size="small" aria-label="products database">
            <TableHead sx={tableStyles.tableHead}>
              <TableRow>
                <TableCell sx={{ ...tableStyles.tableCell, ...cellStyles.header, width: '25%', ...cellStyles.center, textTransform: 'none' }}>Product</TableCell>
                <TableCell sx={{ ...tableStyles.tableCell, ...cellStyles.header, width: '15%', ...cellStyles.center, textTransform: 'none' }}>Rarity</TableCell>
                <TableCell sx={{ ...tableStyles.tableCell, ...cellStyles.header, width: '20%', ...cellStyles.center, textTransform: 'none' }}>Production Time</TableCell>
                <TableCell sx={{ ...tableStyles.tableCell, ...cellStyles.header, width: '15%', ...cellStyles.center, textTransform: 'none' }}>Biopoints</TableCell>
                <TableCell sx={{ ...tableStyles.tableCell, ...cellStyles.header, width: '15%', ...cellStyles.center, textTransform: 'none' }}>BP/m</TableCell>
                <TableCell sx={{ ...tableStyles.tableCell, ...cellStyles.header, width: '20%', ...cellStyles.center, textTransform: 'none' }}>Source Animal</TableCell>
              </TableRow>
            </TableHead>
            <TableBody sx={tableStyles.tableBody}>
              {filteredProducts.flatMap(product => 
                product.variants.map((variant, index) => (
                  <TableRow key={`${product.id}-${variant.rarity}`}>
                    <TableCell sx={{ ...tableStyles.tableCell, ...cellStyles.base, ...cellStyles.center, height: 80, minHeight: 80, maxHeight: 80, p: 1 }}>
                      <Box sx={{ 
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '100%',
                        width: '100%',
                        textAlign: 'center'
                      }}>
                        {/* Product name removed for all products */}
                        <Tooltip 
                          title={`View ${product.name} (${variant.rarity})`}
                          arrow
                          componentsProps={{
                            tooltip: tooltipStyles.tooltip,
                            arrow: tooltipStyles.arrow,
                          }}
                        >
                          <Box>
                            <RarityAvatar 
                              src={`/images/vegetables/${product.id === 'eggs' ? 'egg' : product.id === 'wool' ? 'fuzzies' : product.id}/${variant.rarity.toLowerCase()}/preview.png`}
                              alt={`${product.name} (${variant.rarity})`}
                              size={40}
                              sx={{ 
                                ...avatarStyles.standard,
                                margin: '0 auto',
                                display: 'block'
                              }}
                              aria-label={`${product.name} (${variant.rarity})`}
                            />
                          </Box>
                        </Tooltip>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ ...tableStyles.tableCell, textAlign: 'center', height: 60, minHeight: 60, maxHeight: 60 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 40 }}>
                        <RarityBadge 
                          rarity={variant.rarity}
                          size="small"
                        />
                      </Box>
                    </TableCell>
                    <TableCell sx={{ ...tableStyles.tableCell, ...cellStyles.base, ...cellStyles.center, height: 60, minHeight: 60, maxHeight: 60 }}>
                      <Typography sx={{ ...typography.number, fontSize: '1rem' }}>
                        {formatProductionTime(variant.productionTime)}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ ...tableStyles.tableCell, ...cellStyles.base, ...cellStyles.center, height: 60, minHeight: 60, maxHeight: 60 }}>
                      <Typography sx={typography.number}>
                        {variant.biopoints}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ ...tableStyles.tableCell, ...cellStyles.base, ...cellStyles.center, height: 60, minHeight: 60, maxHeight: 60 }}>
                      <Typography sx={typography.number}>
                        {((variant.biopoints / (variant.productionTime / 60))).toFixed(1)}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ ...tableStyles.tableCell, textAlign: 'center', height: 60, minHeight: 60, maxHeight: 60 }}>
                      <Tooltip title={`View ${product.sourceAnimals[0]} animals`} arrow>
                        <Link 
                          component={RouterLink}
                          to={`/animals#${animalNameToId[product.sourceAnimals[0]] || ''}`}
                          style={{ textDecoration: 'none' }}
                        >
                          <Box 
                            sx={{ 
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              '&:hover': {
                                opacity: 0.8
                              }
                            }}
                          >
                            <RarityAvatar 
                              src={`/images/${productToAnimalMap[product.id] === 'fuzzies' || productToAnimalMap[product.id] === 'piggies' ? 'beds' : 'animals'}/${productToAnimalMap[product.id]}/common/preview.png`}
                              alt={product.sourceAnimals[0]}
                              size={40}
                              sx={{ ...avatarStyles.standard }}
                              aria-label={product.sourceAnimals[0]}
                            />
                          </Box>
                        </Link>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Container>
    </Box>
  );
};

export default Products;